import { useEffect, useState } from "react";
import { Instagram, Loader2, ExternalLink, RefreshCw, Check, BadgeDollarSign } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { socialIgComments, socialIgImportComment, searchProducts, reviewShareClaims } from "@/lib/api";

function CommentRow({ c, products, onImport }) {
  const [pid, setPid] = useState("");
  const [rating, setRating] = useState(5);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(!!c.imported);
  async function go() {
    if (!pid) { toast.error("Pick the product this comment is about"); return; }
    setBusy(true);
    try { await onImport(c, pid, rating); setDone(true); } finally { setBusy(false); }
  }
  return (
    <li className="rounded-xl border border-zinc-200 bg-white p-3 space-y-2" data-testid={`ig-comment-${c.comment_id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-xs font-semibold text-zinc-800">@{c.username} <span className="text-zinc-400 font-normal">· {c.timestamp ? new Date(c.timestamp).toLocaleDateString() : ""}{c.likes ? ` · ♥ ${c.likes}` : ""}</span></div>
          <div className="text-sm text-zinc-700 whitespace-pre-wrap break-words">{c.text}</div>
          {c.media_caption && <div className="text-[11px] text-zinc-400 truncate mt-0.5">on: {c.media_caption}</div>}
        </div>
        {c.media_permalink && (
          <a href={c.media_permalink} target="_blank" rel="noreferrer" className="text-pink-600 shrink-0" title="Open post"><ExternalLink size={14} /></a>
        )}
      </div>
      {done ? (
        <div className="text-[11px] font-semibold text-emerald-700 inline-flex items-center gap-1" data-testid={`ig-comment-${c.comment_id}-imported`}><Check size={12} /> Imported as a review</div>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <select value={pid} onChange={(e) => setPid(e.target.value)} className="h-8 rounded-lg border border-zinc-200 text-xs px-2 max-w-[220px]" data-testid={`ig-comment-${c.comment_id}-product`}>
            <option value="">Product…</option>
            {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <select value={rating} onChange={(e) => setRating(Number(e.target.value))} className="h-8 rounded-lg border border-zinc-200 text-xs px-2" data-testid={`ig-comment-${c.comment_id}-rating`}>
            {[5, 4, 3].map((n) => <option key={n} value={n}>{n}★</option>)}
          </select>
          <Button size="sm" onClick={go} disabled={busy} className="h-8 rounded-lg bg-pink-600 hover:bg-pink-700 text-white text-xs" data-testid={`ig-comment-${c.comment_id}-import`}>
            {busy ? <Loader2 className="animate-spin" size={12} /> : "Approve as review"}
          </Button>
        </div>
      )}
    </li>
  );
}

export default function InstagramImportPanel({ userRecordId }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [comments, setComments] = useState(null);
  const [products, setProducts] = useState([]);
  const [claims, setClaims] = useState(null);
  const [note, setNote] = useState("");

  async function load() {
    setLoading(true);
    try {
      const [c, p, cl] = await Promise.all([socialIgComments(userRecordId), searchProducts("", 50), reviewShareClaims(userRecordId)]);
      setComments(c.comments || []);
      setNote(c.note || "");
      setProducts(p.products || []);
      setClaims(cl.claims || []);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not load Instagram comments");
      setComments([]);
    } finally { setLoading(false); }
  }
  useEffect(() => { if (open && comments === null) load(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  async function importOne(c, pid, rating) {
    try {
      await socialIgImportComment(userRecordId, { comment_id: c.comment_id, text: c.text, username: c.username, media_permalink: c.media_permalink, product_record_id: pid, rating });
      toast.success(`Review from @${c.username} added`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
      throw e;
    }
  }

  return (
    <div className="mt-4 pt-4 border-t border-zinc-100" data-testid="ig-import-panel">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Instagram size={15} className="text-pink-600" />
          <span className="text-sm font-semibold text-zinc-800">Instagram comments → reviews · share spot-checks</span>
        </div>
        <Button size="sm" variant="outline" className="h-8 rounded-lg text-xs" onClick={() => { if (open) load(); else setOpen(true); }} data-testid="ig-import-toggle">
          {loading ? <Loader2 className="animate-spin" size={13} /> : open ? <><RefreshCw size={13} className="mr-1" /> Refresh</> : "Show"}
        </Button>
      </div>
      {open && (
        <div className="mt-3 space-y-4">
          <div>
            <div className="text-xs font-medium text-zinc-600 mb-2">Recent comments on @orevital posts — approve the good ones as product reviews</div>
            {comments === null || loading ? null : comments.length === 0 ? (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg p-2" data-testid="ig-comments-note">{note || "No comments found on recent posts."}</p>
            ) : (
              <ul className="space-y-2 max-h-[420px] overflow-y-auto pr-1" data-testid="ig-comments-list">
                {comments.map((c) => <CommentRow key={c.comment_id} c={c} products={products} onImport={importOne} />)}
              </ul>
            )}
          </div>
          <div>
            <div className="text-xs font-medium text-zinc-600 mb-2 flex items-center gap-1"><BadgeDollarSign size={13} className="text-amber-600" /> Share reward claims (latest 100)</div>
            {claims && claims.length === 0 && <p className="text-xs text-zinc-400">No claims yet.</p>}
            {claims && claims.length > 0 && (
              <ul className="divide-y divide-zinc-100 rounded-xl border border-zinc-200 bg-white" data-testid="share-claims-list">
                {claims.map((k, i) => (
                  <li key={i} className="p-2.5 text-xs flex flex-wrap items-center gap-x-3 gap-y-1">
                    <span className="font-semibold text-zinc-800">{k.customer || k.user_record_id}</span>
                    <span className="text-zinc-500">{k.product}</span>
                    <span className="text-zinc-400">{k.at ? new Date(k.at).toLocaleString() : ""}</span>
                    <span className={`font-semibold ${k.points_awarded > 0 ? "text-emerald-700" : "text-zinc-400"}`}>{k.points_awarded > 0 ? `$${k.points_awarded}` : "no credit"}</span>
                    <span className="text-zinc-400">{k.network}</span>
                    {k.proof_url ? (
                      <a href={k.proof_url} target="_blank" rel="noreferrer" className="text-pink-700 underline inline-flex items-center gap-1">post link <ExternalLink size={10} /></a>
                    ) : <span className="text-zinc-300">no link</span>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
